import useMediaQuery from '../../Hooks/useMediaQuery';
import { Spin, Tooltip } from 'antd';
import { ArrowUp, ArrowDown, Info } from '../../assets/icons';
import './SMSummary.scss';
import SecondaryDetailsChart from '../Charts/SecondaryDetailsChart';
import { useSelector } from 'react-redux';
import React, { useMemo, useState } from 'react';
import { getDateFormat } from '../../utils';
import { getUsCurrencyFormat, getUsPercentageFormat } from '../../utils/usCurrencyFormatter';
import { toastNotify } from '../../utils/toastNotify';
import { useParams } from 'react-router-dom';
import { getChartData } from '../../services/PropertyDetailsAPI';

const TIMELINE_LABELS = [
  // '1D',
  '5D',
  '30D',
  '6M',
  'YTD',
  '1Y',
  'MAX',
];
const DURATION_LABELS = {
  // '1D': 'today',
  '5D': 'prev 5d',
  '30D': 'prev 30d',
  '6M': 'prev 6M',
  YTD: 'year to date',
  '1Y': 'prev 1Y',
  MAX: 'all time',
};

const SMSummary = (props) => {
  const { propertyId } = useParams();

  const [isFetchingChart, setIsFetchingChart] = useState(false);
  const [timelineChartData, setTimelineChartData] = useState(null);

  const screenSM = useMediaQuery('(min-width: 640px)');
  const { loading = true, data: { data: { summary = {} } = {} } = {} } = useSelector((state) => state.tradeDetails);

  const [selectedTimeline, setSelectedTimeline] = useState('30D');

  const chartData = useMemo(() => {
    if (!loading && !isFetchingChart) {
      let options = {
        month: 'short',
        day: 'numeric',
      };
      switch (selectedTimeline) {
        case '1D':
          options = {
            hour12: true,
            hour: 'numeric',
            hourCycle: 'h12',
          };

          break;
        case '5D':
          options = {
            month: 'short',
            day: 'numeric',
          };

          break;
        case '6M':
          options = {
            month: 'short',
            year: '2-digit',
          };

          break;
        case 'YTD':
          options = {
            month: 'short',
          };

          break;
        case '1Y':
          options = {
            month: 'short',
            year: '2-digit',
          };

          break;
        case 'MAX':
          options = {
            year: 'numeric',
          };

          break;

        default:
          break;
      }
      if (selectedTimeline === '30D') {
        return (
          summary?.chartData30Day?.map((x, index) => ({
            price: Number(x?.pricePerShare) || 0,
            fullDate: getDateFormat({
              date: new Date(x?.date),
              options: {
                day: 'numeric',
                month: 'short',
                year: '2-digit',
              },
            }),
            day: getDateFormat({
              date: new Date(x?.date),
              options: {
                month: 'short',
                day: 'numeric',
              },
            }),
          })) || []
        );
      }
      return (
        timelineChartData?.data?.map((x) => ({
          price: Number(x?.price) || 0,
          fullDate: getDateFormat({
            date: new Date(x?.date),
            options: {
              day: 'numeric',
              month: 'short',
              year: '2-digit',
            },
          }),
          day: getDateFormat({
            date: new Date(x?.date),
            options: options,
          }),
        })) || []
      );
    }
    return [];
  }, [selectedTimeline, loading, isFetchingChart, timelineChartData]);

  const appreciationData = useMemo(() => {
    if (!loading && !isFetchingChart) {
      if (selectedTimeline === '30D') {
        const appreciation = Number(summary.appreciation) || 0;
        const change = appreciation > 0 ? 'growth' : 'loss';
        return {
          appreciation: getUsCurrencyFormat(appreciation),
          change,
          appreciationPtage: getUsPercentageFormat(Number(summary.appreciationPtage) || 0),
        };
      }
      return {
        appreciation: Number(timelineChartData?.appreciation || 0),
        change: Number(timelineChartData?.appreciation || 0) > 0 ? 'growth' : 'loss',
        appreciationPtage: getUsPercentageFormat(Number(timelineChartData?.appreciationPtage) || 0),
      };
    }
    return {
      appreciation: 0,
      change: 'growth',
      appreciationPtage: 0,
    };
  }, [loading, selectedTimeline, isFetchingChart, timelineChartData]);

  const handleSelectTimeline = async (label) => {
    try {
      setSelectedTimeline(label);
      if (label !== '30D') {
        setIsFetchingChart(true);
        const data = await getChartData({ id: propertyId, type: label.toUpperCase() });
        setTimelineChartData(data?.data);
      }
    } catch (err) {
      toastNotify('error', err?.response?.data?.message || 'Failed getting chart data');
    } finally {
      setIsFetchingChart(false);
    }
  };
  return (
    <div className='mb-42 smsummary'>
      <div className='smsummary__numbers'>
        <span className='h2 current'>${chartData?.length ? getUsCurrencyFormat(chartData[chartData.length - 1].price) : 0}</span>
        {!screenSM && (
          <span className={`smsummary__numbers--mobile ${appreciationData.change}`}>
            <span className='value'>{appreciationData.appreciation}</span>
            <span className='percentage'>({appreciationData.appreciationPtage})</span>
            <span className='duration'>
              {appreciationData.change === 'growth' ? <ArrowUp verticalAlign='-2px' /> : <ArrowDown verticalAlign='-2px' />}
              {DURATION_LABELS?.[selectedTimeline] || ''}
            </span>
          </span>
        )}
        {!!screenSM && (
          <span className={`smsummary__numbers--desktop ${appreciationData?.change}`}>
            <span className='percentage'>
              {appreciationData.change === 'growth' ? (
                <ArrowUp verticalAlign='-2px' marginRight='8px' />
              ) : (
                <ArrowDown verticalAlign='-2px' marginRight='8px' />
              )}
              {getUsCurrencyFormat(appreciationData?.appreciationPtage?.toString()?.replace(',',''))}%
            </span>
            <span className='value'>{appreciationData.appreciation}</span>
            <span className='duration'>{DURATION_LABELS?.[selectedTimeline] || ''}</span>
          </span>
        )}
      </div>
      <div className='smsummary__graph'>
        <div className='graph'>
          <div className='timeline'>
            {React.Children.toArray(
              TIMELINE_LABELS.map((label) => {
                return (
                  <span
                    onClick={() => {
                      handleSelectTimeline(label);
                    }}
                    className={`timeline__item ${label === selectedTimeline ? 'active' : ''}`}
                  >
                    {label}
                  </span>
                );
              })
            )}
          </div>

          <Spin spinning={isFetchingChart} wrapperClassName='graphSpin'>
            <SecondaryDetailsChart data={chartData} color={appreciationData.appreciation > 0 ? '#2aa47f' : '#dd0d33'} />
          </Spin>
        </div>
        <div className='data'>
          <p className='fw-600 fz-16'>Key Data Points</p>
          {React.Children.toArray(
            props.points.map((data) => {
              let value = '';
              switch (data.value) {
                case 'lastDividend':
                  value = `$${getUsCurrencyFormat(Number(summary?.lastDividend) || 0)} (${getUsCurrencyFormat(
                    Number(summary?.lastDividendPtage) || 0
                  )}%)`;
                  break;
                case 'nextDividendDate':
                  let date = new Date(summary?.[data.value]);
                  value = getDateFormat({ date, options: { locales: 'en-US', month: 'short', day: 'numeric' } });
                  break;
                default:
                  value = getUsCurrencyFormat(Number(summary?.[data.value]) || 0);
                  break;
              }
              if (data?.prefix) {
                value = `${value}${data?.prefix}`;
              }
              return (
                <div className='data__item'>
                  <Tooltip title={data.tooltip}>
                    <span className={!!data.tooltip ? 'data__item__label tooltip' : 'data__item__label'}>
                      {data.label}{' '}
                      {data.tooltip && (
                        <span>
                          <Info marginLeft='8px' verticalAlign='sub' />
                        </span>
                      )}
                    </span>
                  </Tooltip>
                  <span className='data__item__value'>{value}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* <hr className='mt-32 mb-32 hr' />
      <div className='mb-48 smsummary__table'>
        <p className='subhead-2'>
          The Cooper vs Lincoln, MA{' '}
          <Tooltip
            title={
              <>
                <p>The historical return for this property compared to its market.</p>
                <p className='mb-0'>*Past performance does not guarantee future returns.</p>
                <p className='mb-0'>**For more information, refer to the Offering Circular.</p>
              </>
            }
          >
            <span className='cursor-pointer'>
              <Info marginLeft='8px' color='#726460' verticalAlign='middle' />
            </span>
          </Tooltip>
        </p>
        <Table
          scroll={{
            x: 620,
          }}
          className='compare-table'
          dataSource={props.dataSource}
          columns={props.columns}
          pagination={false}
        />
      </div> */}
    </div>
  );
};

export default SMSummary;
